import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import ActiveWorkoutInterface from '../workout/ActiveWorkoutInterface';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth headers helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============================================
// SESSION STATE CONSTANTS
// ============================================
const SESSION_STATE = {
  NOT_STARTED: 'not_started',
  ACTIVE: 'active',
  COMPLETED: 'completed'
};

// ============================================
// SAFARI/TABLET COMPATIBILITY HELPERS
// ============================================
// Debounce helper to prevent double-tap issues on iOS Safari
const useDebounce = (callback, delay = 300) => {
  const timeoutRef = useRef(null);
  const lastCallRef = useRef(0);
  
  return useCallback((...args) => {
    const now = Date.now();
    // Prevent rapid successive calls (common on iOS touch)
    if (now - lastCallRef.current < delay) {
      console.log('[DEBOUNCE] Blocked rapid call');
      return;
    }
    lastCallRef.current = now;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Execute immediately but block subsequent calls
    callback(...args);
  }, [callback, delay]);
};

// ============================================
// START TRAINING CONFIRMATION SCREEN
// ============================================
const StartTrainingScreen = ({ workout, booking, onStart, onCancel, isStarting }) => {
  const exerciseCount = workout?.exercises?.length || 0;
  
  // Calculate total target sets and volume (support legacy field names)
  const totalTargetSets = workout?.exercises?.reduce((sum, ex) => sum + (ex.target_sets || ex.sets || 3), 0) || 0;
  const supersetCount = workout?.exercises?.filter(ex => ex.is_superset)?.length || 0;
  
  // Touch/click handler with explicit touch-action for Safari
  const handleStartClick = useCallback((e) => {
    // Prevent double-tap zoom on iOS Safari
    e.preventDefault();
    if (!isStarting && exerciseCount > 0) {
      onStart();
    }
  }, [isStarting, onStart, exerciseCount]);
  
  const handleCancelClick = useCallback((e) => {
    e.preventDefault();
    if (!isStarting) {
      onCancel();
    }
  }, [isStarting, onCancel]);
  
  // Show message if no exercises planned
  if (exerciseCount === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900 overflow-y-auto flex items-start sm:items-center justify-center p-4" style={{ touchAction: 'manipulation' }}>
        <div className="max-w-lg w-full my-auto">
          <div className="glass-card p-5 sm:p-8 text-center">
            <div className="text-6xl mb-6">📋</div>
            <h1 className="text-2xl font-bold text-white mb-4">
              Sin ejercicios planificados
            </h1>
            <p className="text-purple-300 mb-6">
              Tu entrenador aún no ha añadido ejercicios para esta sesión.
              Contacta con tu entrenador para que planifique tu entrenamiento.
            </p>
            <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-yellow-200 text-sm">
                💡 <strong>Tip:</strong> Tu entrenador puede añadir ejercicios desde el panel de administración.
              </p>
            </div>
            <button
              onClick={handleCancelClick}
              onTouchEnd={handleCancelClick}
              className="px-8 py-4 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold transition"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              ← Volver
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 z-50 bg-slate-900 overflow-y-auto flex items-start sm:items-center justify-center p-4" style={{ touchAction: 'manipulation' }}>
      <div className="max-w-lg w-full my-auto">
        <div className="glass-card p-5 sm:p-8 text-center">
          {/* Icon */}
          <div className="text-6xl mb-6">🏋️</div>
          
          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-2">
            ¿Listo para entrenar?
          </h1>
          <p className="text-purple-300 mb-6">
            {booking?.date} • {booking?.start_time} - {booking?.end_time}
          </p>
          
          {/* Workout Summary */}
          <div className="bg-purple-900/30 rounded-xl p-4 mb-6">
            <p className="text-purple-200 text-sm mb-3">Tu sesión incluye:</p>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-blue-500/20 rounded-lg p-3">
                <p className="text-blue-200 text-2xl font-bold">{exerciseCount}</p>
                <p className="text-blue-300 text-xs">ejercicios</p>
              </div>
              <div className="bg-green-500/20 rounded-lg p-3">
                <p className="text-green-200 text-2xl font-bold">{totalTargetSets}</p>
                <p className="text-green-300 text-xs">series totales</p>
              </div>
              {supersetCount > 0 && (
                <div className="bg-orange-500/20 rounded-lg p-3">
                  <p className="text-orange-200 text-2xl font-bold">{supersetCount}</p>
                  <p className="text-orange-300 text-xs">superseries</p>
                </div>
              )}
            </div>
            
            {/* Exercise preview */}
            <div className="flex flex-wrap justify-center gap-2">
              {workout?.exercises?.slice(0, 4).map((ex, idx) => (
                <span key={idx} className="inline-block bg-purple-800/40 text-purple-200 text-sm px-3 py-1 rounded-full">
                  {ex.exercise_name_es || ex.exercise_name}
                </span>
              ))}
              {exerciseCount > 4 && (
                <span className="text-purple-400 text-sm px-3 py-1">
                  +{exerciseCount - 4} más
                </span>
              )}
            </div>
          </div>
          
          {/* Reintroduction week note (first session back) */}
          {workout?.reintro_client_note && (
            <div className="text-left bg-amber-500/15 rounded-lg p-4 mb-4 border border-amber-400/30" data-testid="reintro-client-note">
              <p className="text-amber-200 text-sm">🌅 {workout.reintro_client_note}</p>
            </div>
          )}

          {/* Info */}
          <div className="text-left bg-blue-900/20 rounded-lg p-4 mb-6 border border-blue-500/20">
            <p className="text-blue-300 text-sm mb-2">📋 Al iniciar:</p>
            <ul className="text-blue-200 text-sm space-y-1">
              <li>• El cronómetro de sesión comenzará</li>
              <li>• Podrás registrar tus series y pesos</li>
              <li>• Temporizador de descanso automático</li>
              <li>• Tu progreso se guardará automáticamente</li>
            </ul>
          </div>
          
          {/* Actions - with touch-action for Safari */}
          <div className="flex gap-4">
            <button
              onClick={handleCancelClick}
              onTouchEnd={handleCancelClick}
              disabled={isStarting}
              className="flex-1 px-6 py-4 rounded-xl bg-gray-700 hover:bg-gray-600 text-gray-200 font-semibold transition disabled:opacity-50 select-none"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              data-testid="cancel-start-btn"
            >
              ← Volver
            </button>
            <button
              onClick={handleStartClick}
              onTouchEnd={handleStartClick}
              disabled={isStarting}
              className="flex-1 px-6 py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold text-lg transition shadow-lg shadow-green-500/30 disabled:opacity-50 select-none"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              data-testid="start-workout-btn"
            >
              {isStarting ? '⏳ Iniciando...' : '🟢 Comenzar entrenamiento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// CLIENT WORKOUT EXECUTION (FULL SCREEN WRAPPER)
// ============================================
const ClientWorkoutExecution = ({
  booking,
  workout,
  onFinish,
  onClose,
  language = 'es',
  t = (key) => key
}) => {
  // Session state
  const [sessionState, setSessionState] = useState(SESSION_STATE.NOT_STARTED);
  const [isStarting, setIsStarting] = useState(false);
  
  // Workout data for ActiveWorkoutInterface
  const [workoutData, setWorkoutData] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [recoveryDetected, setRecoveryDetected] = useState(false);
  
  // Initialize workout data from workout exercises (with recovery detection)
  useEffect(() => {
    if (workout?.exercises) {
      // Map exercises with existing actual_sets (from backend - already saved progress)
      const exercisesWithProgress = workout.exercises.map(ex => ({
        ...ex,
        actual_sets: ex.actual_sets || [],
        completed: false
      }));
      
      setWorkoutData(exercisesWithProgress);
      
      // RECOVERY: Check if session was already started and has progress
      // Check both started_at timestamp AND status field for robustness
      if ((workout.started_at || workout.status === 'in_progress') && !workout.is_finished) {
        setSessionState(SESSION_STATE.ACTIVE);
        
        // Detect if there's saved progress (actual_sets data)
        const hasProgress = exercisesWithProgress.some(ex => ex.actual_sets && ex.actual_sets.length > 0);
        if (hasProgress) {
          setRecoveryDetected(true);
          console.log('[RECOVERY] Detected in-progress workout with saved data');
          
          // Find the last exercise with incomplete sets to resume from
          let resumeIndex = 0;
          for (let i = 0; i < exercisesWithProgress.length; i++) {
            const ex = exercisesWithProgress[i];
            const targetSets = ex.target_sets || ex.sets || 3;
            const completedSets = ex.actual_sets?.length || 0;
            
            if (completedSets > 0 && completedSets < targetSets) {
              // This exercise is in progress
              resumeIndex = i;
              break;
            } else if (completedSets >= targetSets && i < exercisesWithProgress.length - 1) {
              // This exercise is complete, move to next
              resumeIndex = i + 1;
            }
          }
          setCurrentExerciseIndex(resumeIndex);
          console.log(`[RECOVERY] Resuming from exercise ${resumeIndex + 1}`);
        }
      }
    }
  }, [workout]);
  
  // Ref to prevent double-submit on Safari/iOS touch events
  const isSubmittingRef = useRef(false);
  
  // Start training session - with Safari/iOS double-tap protection
  const handleStartTraining = useCallback(async () => {
    // Prevent double-submission (critical for Safari touch events)
    if (isSubmittingRef.current || isStarting) {
      console.log('[SAFARI] Blocked duplicate start attempt');
      return;
    }
    
    isSubmittingRef.current = true;
    setIsStarting(true);
    
    try {
      console.log('[START] Initiating workout start for:', workout?.id);
      
      // Validate workout data is ready
      if (!workout?.id) {
        throw new Error('Workout ID is missing');
      }
      
      // Call backend to start session
      const response = await axios.post(`${API}/workouts/${workout.id}/start`, {}, { headers: getAuthHeaders() });
      console.log('[START] Backend response:', response.data);
      
      // Wait for state to settle before transitioning (helps Safari)
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setSessionState(SESSION_STATE.ACTIVE);
    } catch (error) {
      console.error('[START] Error starting training session:', error);
      
      // Log detailed error for debugging
      if (error.response) {
        console.error('[START] Backend error:', error.response.status, error.response.data);
      }
      
      // Still allow starting even if backend call fails (offline fallback)
      // But only if we have workout data
      if (workout?.exercises?.length > 0) {
        console.log('[START] Proceeding with local state despite backend error');
        setSessionState(SESSION_STATE.ACTIVE);
      } else {
        alert('Error al iniciar el entrenamiento. Por favor, recarga la página e intenta de nuevo.');
      }
    } finally {
      setIsStarting(false);
      // Release lock after a delay to prevent rapid re-taps
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 500);
    }
  }, [workout?.id, workout?.exercises?.length, isStarting]);
  
  // Handle workout cancellation
  const handleCancelWorkout = useCallback(() => {
    if (window.confirm('¿Estás seguro de cancelar el entrenamiento?\n\nTu progreso se perderá.')) {
      onClose();
    }
  }, [onClose]);
  
  // Handle workout completion from ActiveWorkoutInterface
  const handleFinishWorkout = useCallback(async (timeData) => {
    timeData = timeData || {};
    // Retry wrapper: rides over transient network blips / 5xx / origin 520-502
    // (e.g. a node reschedule) so finishing doesn't throw a scary popup when the
    // only problem was a momentary hiccup. Client (4xx) errors are not retried.
    const withRetry = async (fn, tries = 3) => {
      let lastErr;
      for (let i = 0; i < tries; i++) {
        try {
          return await fn();
        } catch (e) {
          lastErr = e;
          const st = e?.response?.status;
          const retryable = !st || st >= 500 || st === 408 || st === 429;
          if (!retryable || i === tries - 1) throw e;
          await new Promise((r) => setTimeout(r, 500 * (i + 1)));
        }
      }
      throw lastErr;
    };

    // Helper function to format time to nearest 0.5 minute
    const formatToHalfMinutes = (seconds) => {
      const totalMinutes = seconds / 60;
      return Math.round(totalMinutes * 2) / 2;
    };
    const duration = formatToHalfMinutes(timeData.effectiveTrainingTime || 0);
    const total_time = formatToHalfMinutes(timeData.totalDuration || 0);
    const rest_time = formatToHalfMinutes(timeData.totalRestTime || 0);
    const localDate = (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();

    // Best-effort: persist sets + metadata. These are NON-critical for marking the
    // session finished — actual_sets are already autosaved continuously during the
    // workout, and /finish stamps the times too. If they blip we log and move on
    // (no popup) rather than abort the whole finish.
    try {
      const formattedExercises = workoutData.map(ex => ({
        exercise_id: ex.exercise_id || '',
        exercise_name: ex.exercise_name || 'Unknown',
        sets: ex.target_sets || ex.sets || 3,
        target_sets: ex.target_sets || ex.sets || 3,
        reps: ex.target_reps || ex.reps || 12,
        target_reps: ex.target_reps || ex.reps || 12,
        weight: ex.target_weight || ex.weight || 0,
        target_weight: ex.target_weight || ex.weight || 0,
        rest_time: ex.rest_time || 120,
        notes: ex.notes || '',
        note: ex.note || '',
        image_url: ex.image_url || '',
        animation_webp: ex.animation_webp || '',
        instructions: ex.instructions || '',
        muscle_groups: ex.muscle_groups || [],
        equipment: ex.equipment || '',
        is_superset: ex.is_superset || false,
        superset_group: ex.superset_group || 'A',
        actual_sets: ex.actual_sets || []
      }));
      await withRetry(() => axios.put(`${API}/workouts/${workout.id}/save-progress`, formattedExercises, { headers: getAuthHeaders() }));
    } catch (e) {
      console.warn('[FINISH] save-progress failed (non-critical, sets already autosaved):', e?.message);
    }

    try {
      await withRetry(() => axios.put(`${API}/workouts/${workout.id}`, {
        duration,
        total_time,
        rest_time,
        session_rating: timeData.session_rating || null,
        exercise_feedback: timeData.exercise_feedback || {},
        session_notes: timeData.session_notes || null
      }, { headers: getAuthHeaders() }));
    } catch (e) {
      console.warn('[FINISH] metadata update failed (non-critical):', e?.message);
    }

    // CRITICAL: mark the workout finished. This is the only call that must land
    // for the session to leave "En curso". Retried on transient failures.
    try {
      await withRetry(() => axios.post(`${API}/workouts/${workout.id}/finish`, {
        total_time,
        total_rest_time: rest_time,
        effective_time: duration,
        local_date: localDate,
      }, { headers: getAuthHeaders() }));

      setSessionState(SESSION_STATE.COMPLETED);
      // NOTE: navigation/close is intentionally NOT done here. The workout is
      // now persisted as finished; the caller shows a read-only summary recap
      // and navigates away via onExit.
      return true;
    } catch (error) {
      console.error('Error finishing workout:', error);
      // Only reached if the critical /finish call keeps failing after retries —
      // a genuine problem worth surfacing (not a spurious blip).
      let errorMessage = 'Error desconocido';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (error.message) {
        errorMessage = typeof error.message === 'string' ? error.message : JSON.stringify(error.message);
      }
      alert('No se pudo cerrar el entrenamiento ahora mismo. Tu progreso está guardado; inténtalo de nuevo en unos segundos.\n\n(' + errorMessage + ')');
      return false;
    }
  }, [workout, workoutData]);
  
  // Show start screen if session not started
  if (sessionState === SESSION_STATE.NOT_STARTED) {
    return (
      <StartTrainingScreen
        workout={workout}
        booking={booking}
        onStart={handleStartTraining}
        onCancel={onClose}
        isStarting={isStarting}
      />
    );
  }
  
  // SAFARI FIX: Guard against rendering ActiveWorkoutInterface before data is ready
  // This prevents race conditions where the component mounts before workoutData is populated
  if (!workoutData || workoutData.length === 0) {
    // Check if workout genuinely has no exercises (not just loading)
    if (workout && workout.exercises && workout.exercises.length === 0) {
      // Workout exists but has no exercises planned
      return (
        <div className="fixed inset-0 z-50 bg-slate-900 flex items-center justify-center" style={{ touchAction: 'manipulation' }}>
          <div className="max-w-md mx-4 text-center">
            <div className="glass-card p-8">
              <div className="text-6xl mb-4">📋</div>
              <h2 className="text-2xl font-bold text-white mb-4">Sin ejercicios planificados</h2>
              <p className="text-purple-300 mb-6">
                Tu entrenador aún no ha planificado los ejercicios para esta sesión. 
                Contacta con tu entrenador para que añada los ejercicios.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold transition"
                style={{ touchAction: 'manipulation' }}
              >
                ← Volver
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    // Still loading data
    return (
      <div className="fixed inset-0 z-50 bg-slate-900 flex items-center justify-center" style={{ touchAction: 'manipulation' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-purple-300">Preparando entrenamiento...</p>
        </div>
      </div>
    );
  }
  
  // Full-screen workout execution using ActiveWorkoutInterface
  return (
    <div className="fixed inset-0 z-50 bg-slate-900 overflow-y-auto" style={{ touchAction: 'manipulation' }}>
      <div className="min-h-screen p-4 md:p-6">
        {/* Recovery Banner - Shows when session was recovered */}
        {recoveryDetected && (
          <div 
            className="mb-4 bg-green-900/50 border border-green-400/30 rounded-lg p-3 flex items-center justify-between"
            data-testid="recovery-banner"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">✅</span>
              <div>
                <p className="text-green-200 font-medium">Sesión recuperada</p>
                <p className="text-green-300 text-sm">Tu progreso anterior ha sido restaurado automáticamente</p>
              </div>
            </div>
            <button 
              onClick={() => setRecoveryDetected(false)}
              className="text-green-300 hover:text-white p-1"
              style={{ touchAction: 'manipulation' }}
            >
              ✕
            </button>
          </div>
        )}
        
        <ActiveWorkoutInterface
          workout={workout}
          workoutData={workoutData}
          setWorkoutData={setWorkoutData}
          currentExerciseIndex={currentExerciseIndex}
          setCurrentExerciseIndex={setCurrentExerciseIndex}
          onFinishWorkout={handleFinishWorkout}
          onExit={() => { if (onFinish) onFinish(workout.id); onClose(); }}
          onCancelWorkout={handleCancelWorkout}
          language={language}
          t={t}
        />
      </div>
    </div>
  );
};

export default ClientWorkoutExecution;
